import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap, Power1 } from 'gsap';
import Lottie from 'lottie-react';

import useClickEnter from '@/shared/hooks/useClickEnter';

import PolonaiseLine from '@/features/letterParty/components/startScreen/PolonaiseLine';
import AudioToggleButton from '@/shared/components/audioToggle/AudioToggleButton';
import BackButton from '@/shared/components/button/BackButton';
import Button from '@/shared/components/button/Button';
import Tooltip, { TooltipColors, TooltipSizes } from '@/shared/components/tooltip/Tooltip';

import palmTree from '@/features/letterParty/assets/lottie/palm-tree-level-1.json';
import speaker from '@/features/letterParty/assets/lottie/start-speaker.json';

import '@/features/letterParty/styles/letter-party-start-screen.scss';

interface Props {
	onStart: () => void;
	onBack: () => void;
	bestScore?: number;
}

const TOOLTIP_ANCHOR_ID = 'letter-party-start-screen-tooltip-anchor';

const StartScreen = ({ onStart, onBack, bestScore = 0 }: Props) => {
	const { t } = useTranslation();
	const rootRef = useRef<HTMLDivElement | null>(null);
	const hasStartedRef = useRef(false);

	const handleStart = () => {
		hasStartedRef.current = true;
		const el = rootRef.current;
		if (el) {
			gsap.to(el, {
				opacity: 0,
				duration: 0.3,
				ease: Power1.easeOut,
				onComplete: () => onStart(),
			});
		} else {
			onStart();
		}
	};

	useClickEnter(handleStart);

	return (
		<div
			className="letter-party-game__start-screen"
			role="dialog"
			aria-modal="true"
			aria-label={t('letter_party.start_screen.aria_label')}
			ref={rootRef}
		>
			<div className="letter-party-game__start-screen-background">
				<div className="letter-party-game__start-screen-background-backward" />
				<PolonaiseLine />
				<Lottie
					animationData={palmTree}
					loop
					autoplay
					className="letter-party-game__start-screen-palm-tree-left"
				/>
				<Lottie animationData={speaker} loop autoplay className="letter-party-game__start-screen-speaker" />
				<Lottie
					animationData={palmTree}
					loop
					autoplay
					className="letter-party-game__start-screen-palm-tree-right"
				/>
				<div className="letter-party-game__start-screen-background-forward" />
			</div>

			<div className="letter-party-game__start-screen-controls">
				<div className="container">
					<BackButton onClick={onBack} />
					<AudioToggleButton tooltip={t('letter_party.audio_toggle.tooltip')} isOpen />
				</div>
			</div>

			<div
				id={TOOLTIP_ANCHOR_ID}
				className="letter-party-game__start-screen-tooltip-anchor"
				role="status"
				aria-live="polite"
			/>
			<Tooltip
				place="top"
				title={t('letter_party.start_screen.tooltip')}
				anchor={TOOLTIP_ANCHOR_ID}
				color={TooltipColors.LIGHT}
				size={TooltipSizes.LARGE}
				isOpen
				className="letter-party-game__start-screen-tooltip"
			/>
			<div className="btn-group center letter-party-game__start-screen-bottom">
				<Button
					type="primary"
					iconName="icon-play"
					title={t('letter_party.start_screen.button')}
					onClick={handleStart}
				/>
				{bestScore > 0 && (
					<div className="letter-party-game__start-screen-score">
						<span className="letter-party-game__start-screen-score-text">
							{t('letter_party.start_screen.longest_polonaise')}
						</span>
						<span className="letter-party-game__start-screen-score-number">{bestScore}</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default StartScreen;
